<template>
	<div class="cmt">
		<div class="cmt-toolbar">
			<p class="cmt-hint">
				Cada miembro conserva su propia regla del mayor. Las cuentas ignoradas y los neteos se
				configuran en las tarjetas de abajo.
			</p>
			<button type="button" class="theme_btn_white" :disabled="loading" @click="$emit('manage-members')">
				Gestionar miembros
			</button>
		</div>

		<p v-if="!loading && members.length === 0" class="cmt-empty">
			Esta consolidada no tiene miembros todavía. Agrégalos con «Gestionar miembros».
		</p>

		<table v-else class="cmt-table">
			<thead>
				<tr>
					<th scope="col">Empresa</th>
					<th scope="col">Regla del mayor</th>
					<th scope="col">Tipos de negocio</th>
					<th scope="col" class="is-num">Cuentas ignoradas</th>
					<th scope="col" class="is-num">Neteos</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="member in members" :key="member.company_id">
					<td>
						<span class="cmt-name">{{ member.name }}</span>
						<span class="cmt-rut">{{ member.rut }}</span>
					</td>
					<td>
						<span class="cmt-rule" :class="{ 'cmt-rule--ledger': member.has_ledger }">
							{{ ruleLabel(member) }}
						</span>
					</td>
					<td>
						<span v-if="member.business_types.length === 0" class="cmt-muted">Sin tipo</span>
						<span v-else class="cmt-chips">
							<span v-for="type in member.business_types" :key="type" class="cmt-chip">{{ type }}</span>
						</span>
					</td>
					<td class="is-num">
						<span
							v-if="member.ignored_accounts.length"
							class="cmt-count cmt-count--ignored"
							v-tooltip.top="member.ignored_accounts.join('\n')"
						>
							{{ member.ignored_accounts.length }}
						</span>
						<span v-else class="cmt-muted">0</span>
					</td>
					<td class="is-num">
						<span v-if="member.netting_count" class="cmt-count">{{ member.netting_count }}</span>
						<span v-else class="cmt-muted">0</span>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
// Tabla de miembros de la pestaña «Consolidación» (plan consolidadas, D8).
// Presentacional: recibe `members` como lo devuelve
// complementary_data.members / consolidated_adjustments.members y emite
// `manage-members` para abrir el modal existente. El título y el conteo los
// lleva la tarjeta plegable que la contiene.
export default {
	name: "ConsolidatedMembersTable",
	props: {
		members: {
			type: Array,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	methods: {
		ruleLabel(member) {
			if (!member.has_ledger) return "Solo SII";
			return `Mayor hasta ${this.formatDate(member.ledger_cutoff_date)}`;
		},
		// ISO (aaaa-mm-dd) → dd-mm-aaaa, sin pasar por Date (evita el corrimiento
		// de zona horaria de una fecha sin hora).
		formatDate(iso) {
			if (!iso) return "";
			const [year, month, day] = String(iso).slice(0, 10).split("-");
			return `${day}-${month}-${year}`;
		},
	},
};
</script>

<style lang="scss" scoped>
@use "./account_plan_buttons" as *;

.cmt {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding-top: 12px;
}

.cmt-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
}

.cmt-hint {
	margin: 0;
	max-width: 60ch;
	font-size: 13px;
	line-height: 1.5;
	color: var(--ap-slate);
}

.cmt-empty {
	margin: 0;
	padding: 18px;
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	font-size: 13.5px;
	color: var(--ap-slate);
}

// Tabla sin bordes verticales: reglas horizontales, header uppercase,
// numéricos a la derecha (receta del área analista).
.cmt-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13.5px;

	th,
	td {
		padding: 10px 12px;
		text-align: left;
		vertical-align: top;
		border-bottom: 1px solid var(--ap-line);
	}

	th {
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--ap-muted);
	}

	tbody tr:last-child td {
		border-bottom: 0;
	}

	.is-num {
		text-align: right;
		font-variant-numeric: tabular-nums;
	}
}

.cmt-name {
	display: block;
	font-weight: 600;
	color: var(--ap-ink);
}

.cmt-rut {
	display: block;
	font-size: 12px;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;
}

.cmt-rule {
	display: inline-block;
	padding: 2px 10px;
	border-radius: var(--ap-radius-pill);
	background: var(--ap-mist);
	color: var(--ap-slate);
	font-size: 12.5px;

	&--ledger {
		background: var(--ap-teal-wash);
		color: var(--ap-teal-deep);
	}
}

.cmt-chips {
	display: inline-flex;
	flex-wrap: wrap;
	gap: 4px;
}

.cmt-chip {
	padding: 2px 8px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	font-size: 12px;
	color: var(--ap-ink-2);
}

.cmt-count {
	display: inline-block;
	min-width: 26px;
	padding: 2px 8px;
	border-radius: var(--ap-radius-pill);
	background: var(--ap-teal-wash);
	color: var(--ap-teal-deep);
	font-weight: 600;
	text-align: center;

	&--ignored {
		background: var(--ap-warn-wash);
		color: var(--ap-warn-ink);
		cursor: help;
	}
}

.cmt-muted {
	color: var(--ap-muted);
}

@media (max-width: 860px) {
	.cmt-table {
		display: block;
		overflow-x: auto;
	}
}
</style>
